import type { Asset, AuditEntry, Part, PlantState, Vendor } from "./data";
import { remoteDataEnabled, requireSupabase, supabase } from "./supabaseClient";

function verificationState(value: string): Asset["verificationState"] {
  if (value === "verified") return "Verified";
  if (value === "needs_research") return "Needs Research";
  return "Incomplete";
}

function assetStatus(value: string): Asset["status"] {
  if (value === "out_of_service") return "Out of Service";
  if (value === "spare") return "Spare";
  if (value === "retired") return "Retired";
  return "In Service";
}

function specClass(value: string) {
  if (value === "oem_spec") return "OEM SPEC" as const;
  if (value === "plant_validated") return "PLANT VALIDATED" as const;
  return "AS-BUILT / INSTALLED" as const;
}

function verificationSource(value: string) {
  const map: Record<string, "OEM manual" | "Nameplate" | "Drawing" | "Measured in field" | "Plant estimate" | "Unknown"> = {
    oem_manual: "OEM manual",
    nameplate: "Nameplate",
    drawing: "Drawing",
    measured_in_field: "Measured in field",
    plant_estimate: "Plant estimate",
    unknown: "Unknown",
  };
  return map[value] ?? "Unknown";
}

export async function getRemoteSession() {
  if (!remoteDataEnabled || !supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session;
}

export async function signIn(email: string, password: string) {
  const client = requireSupabase();
  return client.auth.signInWithPassword({ email, password });
}

export async function signUp(email: string, password: string) {
  const client = requireSupabase();
  return client.auth.signUp({ email, password });
}

export async function signOut() {
  const client = requireSupabase();
  return client.auth.signOut();
}

export async function loadRemotePlantState(): Promise<PlantState | null> {
  if (!remoteDataEnabled || !supabase) return null;

  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) return null;

  const [linesRes, assetsRes, specsRes, capsRes, partsRes, bomRes, vendorsRes, altRes, sourceRes, auditRes] = await Promise.all([
    supabase.from("lines").select("*").order("display_order"),
    supabase.from("assets").select("*").order("display_order"),
    supabase.from("specifications").select("*").order("sort_order"),
    supabase.from("capabilities").select("*"),
    supabase.from("parts").select("*"),
    supabase.from("bom_items").select("*"),
    supabase.from("vendors").select("*"),
    supabase.from("part_alternates").select("part_id,alternate_part_id"),
    supabase.from("part_vendors").select("part_id,vendor_id,vendor_sku,quoted_price,lead_time_days,preferred").order("preferred", { ascending: false }),
    supabase.from("audit_log").select("*").order("created_at", { ascending: false }).limit(200),
  ]);

  const error = linesRes.error ?? assetsRes.error ?? specsRes.error ?? capsRes.error ?? partsRes.error ?? bomRes.error ?? vendorsRes.error ?? altRes.error ?? sourceRes.error ?? auditRes.error;
  if (error) throw error;

  const specsByAsset = new Map<string, Asset["specs"]>();
  for (const row of specsRes.data ?? []) {
    const list = specsByAsset.get(row.asset_id) ?? [];
    list.push({
      label: row.label,
      value: row.value_text ?? (row.value_numeric == null ? "Unknown" : String(row.value_numeric)),
      unit: row.unit ?? undefined,
      class: specClass(row.class),
      verification: verificationSource(row.verification),
      note: row.source_note ?? undefined,
    });
    specsByAsset.set(row.asset_id, list);
  }

  const capsByAsset = new Map<string, Asset["capabilities"]>();
  for (const row of capsRes.data ?? []) {
    const list = capsByAsset.get(row.asset_id) ?? [];
    list.push({
      label: row.label,
      value: row.value_text ?? (row.value_numeric == null ? "Unknown" : `${row.value_numeric}${row.unit ? ` ${row.unit}` : ""}`),
    });
    capsByAsset.set(row.asset_id, list);
  }

  const bomByAsset = new Map<string, Asset["bom"]>();
  for (const row of bomRes.data ?? []) {
    const list = bomByAsset.get(row.asset_id) ?? [];
    list.push({ partId: row.part_id, qty: Number(row.quantity), locationNote: row.location_note ?? undefined });
    bomByAsset.set(row.asset_id, list);
  }

  const assets: Asset[] = (assetsRes.data ?? []).map((row) => ({
    id: row.id,
    tag: row.asset_tag,
    lineId: row.line_id ?? "",
    parentId: row.parent_asset_id ?? undefined,
    order: row.display_order,
    name: row.name,
    type: row.equipment_type,
    manufacturer: row.manufacturer ?? "Unknown",
    model: row.model ?? "Unknown",
    serial: row.serial_number ?? "Unknown",
    status: assetStatus(row.status),
    criticality: row.criticality,
    verificationState: verificationState(row.verification_state),
    completeness: row.completeness,
    guideTiId: row.guide_ti_id ?? "",
    specs: specsByAsset.get(row.id) ?? [],
    capabilities: capsByAsset.get(row.id) ?? [],
    bom: bomByAsset.get(row.id) ?? [],
    notes: row.notes ?? undefined,
  }));

  const partAlternates = new Map<string, string[]>();
  for (const row of altRes.data ?? []) {
    const list = partAlternates.get(row.part_id) ?? [];
    if (!list.includes(row.alternate_part_id)) list.push(row.alternate_part_id);
    partAlternates.set(row.part_id, list);
  }

  type PartSource = { vendor_id: string; vendor_sku: string | null; quoted_price: number | string | null; lead_time_days: number | null; preferred: boolean };
  const preferredSourceByPart = new Map<string, PartSource>();
  for (const row of sourceRes.data ?? []) {
    const existing = preferredSourceByPart.get(row.part_id);
    if (!existing || row.preferred) preferredSourceByPart.set(row.part_id, row as PartSource);
  }

  const parts: Part[] = (partsRes.data ?? []).map((row) => {
    const source = preferredSourceByPart.get(row.id);
    return {
      id: row.id,
      internalPn: row.internal_part_number ?? "",
      oemPn: row.oem_part_number ?? "",
      description: row.description,
      manufacturer: row.manufacturer ?? "Unknown",
      category: row.category ?? "Unknown",
      criticality: row.criticality,
      stockQty: Number(row.stock_quantity),
      minQty: Number(row.minimum_quantity),
      location: row.stock_location ?? "Unknown",
      preferredVendorId: source?.vendor_id,
      vendorSku: source?.vendor_sku ?? undefined,
      leadTime: source?.lead_time_days != null ? `${source.lead_time_days} days` : row.lead_time_days == null ? "Unknown" : `${row.lead_time_days} days`,
      lastPrice: source?.quoted_price != null ? Number(source.quoted_price) : row.last_price == null ? undefined : Number(row.last_price),
      alternates: partAlternates.get(row.id) ?? [],
    };
  });

  const vendors: Vendor[] = (vendorsRes.data ?? []).map((row) => ({
    id: row.id,
    name: row.company_name,
    website: row.website ?? "",
    contact: row.contact_name ?? "",
    phone: row.phone ?? "",
    email: row.email ?? "",
    preferred: row.preferred,
    notes: row.notes ?? "",
  }));

  const audit: AuditEntry[] = (auditRes.data ?? []).map((row) => ({
    id: row.id,
    at: row.created_at,
    user: row.user_id ?? "System",
    action: row.action,
    entity: row.entity_type,
    detail: row.reason ?? row.field_name ?? "Database change",
  }));

  const lines = (linesRes.data ?? []).map((row) => {
    const hasAssets = assets.some((asset) => asset.lineId === row.id);
    return {
      id: row.id,
      name: row.name,
      status: hasAssets ? "Active" as const : "Placeholder" as const,
      description: row.description ?? "Awaiting plant walkdown",
      completeness: row.completeness,
    };
  });

  return { lines, assets, parts, vendors, audit };
}
